import React, { useState } from "react";
import { toast } from "sonner";
import { Instagram, Facebook, Linkedin, Send } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiErrorDetail } from "@/lib/apiClient";

const LINKS = [
  "À propos",
  "Mentions légales",
  "Politique de confidentialité",
  "CGU / CGV",
  "Assurances & Garanties décennales",
  "Recrutement Artisans",
];

export const Footer = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/leads", { ...form, project_type: "contact" });
      toast.success(data.message);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer id="contact" className="bg-[#0A0F1A] text-white border-t border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="rounded-3xl bg-gradient-to-r from-[#0D6EFD] to-[#00C3A7] p-8 sm:p-12 mb-16 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/80 mb-3">
            Un projet ? Une question ?
          </p>
          <h3 className="text-2xl sm:text-4xl font-extrabold max-w-3xl mx-auto leading-tight">
            Nos experts sont là pour vous accompagner.
          </h3>
          <Button
            className="mt-8 bg-white text-[#0A0F1A] hover:bg-white/90 rounded-full px-8 h-12 font-semibold"
            data-testid="footer-cta-btn"
            onClick={() => document.getElementById("footer-contact-form")?.scrollIntoView({ behavior: "smooth" })}
          >
            Commencer maintenant
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div>
            <Logo size={30} />
            <p className="mt-5 text-white/60 text-sm leading-relaxed max-w-xs">
              ID-BAT PRO accompagne particuliers et professionnels dans tous leurs projets de
              construction, rénovation et aménagement.
            </p>
            <div className="mt-6 flex gap-3">
              {[Instagram, Facebook, Linkedin].map((Icon, i) => (
                <a key={i} href="#" className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#0D6EFD] flex items-center justify-center transition-colors">
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
            <div className="mt-6 text-sm text-white/50 space-y-1">
              <p><a href="tel:+33782151672" className="hover:text-[#00C3A7] transition-colors">+33 7 82 15 16 72</a></p>
              <p><a href="mailto:id-bat-pro@id-bat-pro.com" className="hover:text-[#00C3A7] transition-colors">id-bat-pro@id-bat-pro.com</a></p>
              <p>www.id-bat-pro.com</p>
              <p>Saint-Raphaël — PACA & France entière</p>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-white/80 mb-4">Informations</h4>
            <ul className="space-y-3">
              {LINKS.map((l) => (
                <li key={l}>
                  <a href="#" className="text-white/60 hover:text-white text-sm transition-colors">{l}</a>
                </li>
              ))}
            </ul>
          </div>

          <div id="footer-contact-form">
            <h4 className="text-sm font-semibold uppercase tracking-wider text-white/80 mb-4">Contactez-nous</h4>
            <form onSubmit={submit} className="space-y-3">
              <Input
                required placeholder="Votre nom" data-testid="footer-name-input"
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
              />
              <Input
                required type="email" placeholder="Votre email" data-testid="footer-email-input"
                value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
              />
              <Input
                placeholder="Téléphone (optionnel)" data-testid="footer-phone-input"
                value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
              />
              <Textarea
                required placeholder="Décrivez votre projet..." rows={3} data-testid="footer-message-input"
                value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
              />
              <Button type="submit" disabled={loading} data-testid="footer-submit-btn"
                className="w-full bg-[#0D6EFD] hover:bg-[#0b5ed7] rounded-full h-11">
                <Send className="w-4 h-4 mr-2" /> {loading ? "Envoi..." : "Envoyer ma demande"}
              </Button>
            </form>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 text-center text-xs text-white/40">
          © 2026 ID-BAT PRO — Tous droits réservés. Vos travaux, enfin simplifiés.
        </div>
      </div>
    </footer>
  );
};
